
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  Stack,
  TextField,
  Grid,
  Alert
} from '@mui/material';
import { OrderFormProps } from '../../lib/order';
import { fetchOrder } from '../../lib/order';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { saveOrder } from '../../lib/order';
import { useApp } from '../../components/AppContext';

export interface RoomRequirmentsSaveData {
  orderId: string;
  requiresBusParking: boolean;
  requiresTransitToVenue: boolean;
  oneBedRoomsNeeded: number;
  twoBedRoomsNeeded: number;
  selectedArrivalDate: string;
  selectedDepartureDate: string;
}


export default function RoomRequirementsForm({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {
  const [requiresBusParking, setRequiresBusParking] = useState(false);
  const [requiresTransitToVenue, setRequiresTransitToVenue] = useState(false);
  const [oneBedRoomsNeeded, setOneBedRoomsNeeded] = useState(0);
  const [twoBedRoomsNeeded, setTwoBedRoomsNeeded] = useState(0);
  const [selectedArrivalDate, setSelectedArrivalDate] = useState('');
  const [selectedDepartureDate, setSelectedDepartureDate] = useState('');
  const [confEarliestArrivalDate, setConfEarliestArrivalDate] = useState('');
  const [confDefaultDepartureDate, setConfDefaultDepartureDate] = useState('');
  const [confDefaultArrivalDate, setConfDefaultArrivalDate] = useState('');
  const { setSnackbarInput } = useApp();


  const handleSaveOrderClick = () => {
      if (!orderId) return;
  
      const saveData: RoomRequirmentsSaveData = {
        orderId,
        requiresBusParking,
        requiresTransitToVenue,
        oneBedRoomsNeeded,
        twoBedRoomsNeeded,
        selectedArrivalDate,
        selectedDepartureDate
      };
  
      saveOrder(saveData)
        .then(success => {
          if (success) {
            setSnackbarInput({ message: 'Room requirements saved successfully', severity: 'success' });
            if (onSave) onSave();
          } else {
            setSnackbarInput({ message: 'Failed to save room requirements', severity: 'error' });
          }
        })
        .catch(error => {
          setSnackbarInput({ message: 'Error saving room requirements', severity: 'error' });
          console.error('Error saving order:', error);
        });
    };

  useEffect(() => {
    fetchOrder(orderId)
    .then(data => {
      if (!data) return;
      setRequiresBusParking(data.requiresBusParking || false);
      setRequiresTransitToVenue(data.requiresTransitToVenue || false);
      setOneBedRoomsNeeded(data.oneBedRoomsNeeded || 0);
      setTwoBedRoomsNeeded(data.twoBedRoomsNeeded || 0);
      setSelectedArrivalDate(data.selectedArrivalDate || data.conference_group.conference.defaultArrivalDate);
      setSelectedDepartureDate(data.selectedDepartureDate || data.conference_group.conference.defaultDepartureDate);
      setConfEarliestArrivalDate(data.conference_group.conference.earliestArrivalDate|| '');
      setConfDefaultDepartureDate(data.conference_group.conference.defaultDepartureDate || '');
      setConfDefaultArrivalDate(data.conference_group.conference.defaultArrivalDate || '');
    })
    .catch(error => {
      console.error('Error fetching order:', error);
    }); 
  }, [orderId]);
  
  const roomRequirementsValid = (oneBedRoomsNeeded || 0) > 0 || (twoBedRoomsNeeded || 0) > 0;

  // Helper function to format date for input field
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().split('T')[0];
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Transportation
          </Typography>
          <Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={requiresBusParking || false}
                  onChange={(e) => setRequiresBusParking(e.target.checked)}
                  name="requiresBusParking"
                />
              }
              label="I need bus parking at the hotel"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={requiresTransitToVenue || false}
                  onChange={(e) => setRequiresTransitToVenue(e.target.checked)}
                  name="requiresTransitToVenue"
                />
              }
              label="I need transportation between the hotel and the conference"
            />
          </Stack>
          </Box>
          
          <Box>
            <Typography variant="body1" color="text.secondary" sx={{ my: 3 }}>
              Rooms
            </Typography>
            <Typography variant="body1" color={roomRequirementsValid ? "text.secondary" : "error"} sx={{ my: 3 }}>
              At least one room is required. Please specify how many 1-bed and/or 2-bed rooms you need.
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Number of 1-Bed Rooms"
                  type="number"
                  value={oneBedRoomsNeeded}
                  onChange={(e) => setOneBedRoomsNeeded(Math.max(0, parseInt(e.target.value)))}
                  helperText="How many rooms with 1 bed do you need?"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Number of 2-Bed Rooms"
                  type="number"
                  value={twoBedRoomsNeeded}
                  onChange={(e) => setTwoBedRoomsNeeded(Math.max(0, parseInt(e.target.value)))}
                  helperText="How many rooms with 2 beds do you need?"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          
          <Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Check In Date"
                  type="date"
                  value={selectedArrivalDate || formatDateForInput(confDefaultArrivalDate)}
                  onChange={(e) => setSelectedArrivalDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formatDateForInput(confEarliestArrivalDate),
                    max: formatDateForInput(confDefaultDepartureDate)
                  }}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Check Out Date"
                  type="date"
                  value={selectedDepartureDate || formatDateForInput(confDefaultDepartureDate)}
                  onChange={(e) => setSelectedDepartureDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: formatDateForInput(confEarliestArrivalDate),
                    max: formatDateForInput(confDefaultDepartureDate)
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {/* Validation Errors */}
          <Box sx={{ mt: 3 }}>
            {((oneBedRoomsNeeded || 0) === 0 && (twoBedRoomsNeeded || 0) === 0) && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select at least one room (1-bed or 2-bed rooms).
              </Alert>
            )}
            {!selectedArrivalDate && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select a check-in date.
              </Alert>
            )}
            {!selectedDepartureDate && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please select a check-out date.
              </Alert>
            )}
          </Box>
      </Stack>
      <Box>
          <Button
            color="primary"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={back} 
            disabled={!back} >
            Back
          </Button>
          <Button
            color="primary"
            size="small"
            variant="contained"
            startIcon={<ArrowForwardIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={handleSaveOrderClick}
            disabled={!roomRequirementsValid || !selectedArrivalDate || !selectedDepartureDate}
          >
            {type === 'step' ? 'Next' : 'Save'}
          </Button>
        </Box>
    </Box>
  );
}